/**
 * StandardText Service
 * API integration for standard text CRUD operations
 *
 * @version 2025.1.0
 * @description Serviço de integração com API de textos padronizados
 */
import apiClient from './apiClient';
import {
  StandardText,
  StandardTextFilters,
  StandardTextFormData,
  StandardTextListResponse,
  StandardTextStats,
  CategoryOption,
  CloneTextData,
  VariableValidation,
  DocumentSectionCatalog,
  DocumentVariable,
  StandardTextPreviewRequest,
  StandardTextPreviewResult,
} from '../types/standardText';

// Re-export types for convenience
export * from '../types/standardText';

export interface StandardTextListParams extends StandardTextFilters {
  page?: number;
  limit?: number;
}

class StandardTextService {
  private baseUrl = '/standard-texts';

  /**
   * Get paginated list of standard texts
   */
  async getStandardTexts(params: StandardTextListParams = {}): Promise<StandardTextListResponse> {
    const { page = 1, limit = 20, ...filters } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters.search) queryParams.append('search', filters.search);
    if (filters.category) queryParams.append('category', filters.category);
    if (filters.companyId) queryParams.append('companyId', filters.companyId.toString());
    if (filters.isGlobal !== undefined) queryParams.append('isGlobal', String(filters.isGlobal));
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

    const response = await apiClient.get(`${this.baseUrl}?${queryParams.toString()}`);

    // Backend returns: { data: { texts, total, page, limit, totalPages } }
    const backendData = response.data.data || response.data;

    return {
      texts: backendData.texts ?? [],
      total: backendData.total ?? 0,
      page: backendData.page || page,
      limit: backendData.limit || limit,
      totalPages: backendData.totalPages || Math.ceil((backendData.total ?? 0) / limit),
    };
  }

  /**
   * Get standard text by ID
   */
  async getStandardTextById(id: string | number): Promise<StandardText> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data || response.data;
  }

  /**
   * Create new standard text
   */
  async createStandardText(data: StandardTextFormData): Promise<StandardText> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data.data || response.data;
  }

  /**
   * Update standard text by ID
   */
  async updateStandardText(id: string | number, data: Partial<StandardTextFormData>): Promise<StandardText> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data.data || response.data;
  }

  /**
   * Delete standard text by ID (soft delete)
   */
  async deleteStandardText(id: string | number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Toggle standard text status (ATIVO <-> INATIVO)
   */
  async toggleStandardTextStatus(id: string | number): Promise<StandardText> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/status`);
    return response.data.data || response.data;
  }

  /**
   * Get all available categories with descriptions
   */
  async getCategories(): Promise<CategoryOption[]> {
    const response = await apiClient.get(`${this.baseUrl}/categories`);
    return response.data.data || response.data;
  }

  /**
   * Catálogo de documentos e suas seções
   *
   * Sai pela rota de textos, e não pela de documentos: quem escreve texto
   * normativo tem `standard_text.*` e pode não ter `document_emission.read`.
   */
  async getDocumentSections(): Promise<DocumentSectionCatalog[]> {
    const response = await apiClient.get(`${this.baseUrl}/document-sections`);
    const payload = response.data.data || response.data;
    return payload.documents ?? [];
  }

  /**
   * Catálogo de variáveis disponíveis nos textos
   */
  async getVariables(): Promise<DocumentVariable[]> {
    const response = await apiClient.get(`${this.baseUrl}/variables`);
    const payload = response.data.data || response.data;
    return payload.variables ?? [];
  }

  /**
   * Prévia do texto com as variáveis resolvidas
   *
   * É POST sem id porque a prévia precisa funcionar para texto ainda não salvo,
   * enquanto a pessoa digita. O HTML volta já sanitizado pelo backend.
   */
  async previewContent(data: StandardTextPreviewRequest): Promise<StandardTextPreviewResult> {
    const body: StandardTextPreviewRequest = {
      content: data.content,
      companyId: data.companyId,
    };
    if (data.departmentId !== undefined) body.departmentId = data.departmentId;
    if (data.sectionKey !== undefined) body.sectionKey = data.sectionKey;

    const response = await apiClient.post(`${this.baseUrl}/preview`, body);
    return response.data.data || response.data;
  }

  /**
   * Get texts by category
   */
  async getByCategory(category: string, companyId?: number, includeGlobal: boolean = true): Promise<StandardText[]> {
    const queryParams = new URLSearchParams();
    if (companyId) queryParams.append('companyId', companyId.toString());
    if (!includeGlobal) queryParams.append('includeGlobal', 'false');

    const query = queryParams.toString();
    const url = `${this.baseUrl}/category/${category}${query ? `?${query}` : ''}`;
    const response = await apiClient.get(url);
    return response.data.data || response.data;
  }

  /**
   * Full text search
   */
  async searchFullText(searchTerm: string, filters?: Partial<StandardTextFilters>, limit: number = 20): Promise<StandardText[]> {
    const queryParams = new URLSearchParams({
      q: searchTerm,
      limit: limit.toString(),
    });

    if (filters?.category) queryParams.append('category', filters.category);
    if (filters?.companyId) queryParams.append('companyId', filters.companyId.toString());
    if (filters?.isGlobal !== undefined) queryParams.append('isGlobal', String(filters.isGlobal));

    const response = await apiClient.get(`${this.baseUrl}/search?${queryParams.toString()}`);
    return response.data.data || response.data;
  }

  /**
   * Get global texts (optionally filtered by category)
   */
  async getGlobalTexts(category?: string): Promise<StandardText[]> {
    const query = category ? `?category=${category}` : '';
    const response = await apiClient.get(`${this.baseUrl}/global${query}`);
    return response.data.data || response.data;
  }

  /**
   * Get company-specific texts (optionally filtered by category)
   */
  async getCompanyTexts(companyId: number, category?: string): Promise<StandardText[]> {
    const query = category ? `?category=${category}` : '';
    const response = await apiClient.get(`${this.baseUrl}/company/${companyId}${query}`);
    return response.data.data || response.data;
  }

  /**
   * Clone text for another company
   */
  async cloneForCompany(sourceTextId: string | number, data: CloneTextData): Promise<StandardText> {
    const response = await apiClient.post(`${this.baseUrl}/${sourceTextId}/clone`, data);
    return response.data.data || response.data;
  }

  // ============================================================================
  // RESERVED FOR FUTURE USE - Variable processing methods
  // These methods are implemented but not exposed in the UI
  // Will be used when StandardText is linked to entities (Consultations, Exams, etc.)
  // ============================================================================

  /**
   * Process text with variable values
   * @reserved Future use - for generating documents from templates
   */
  async processText(textId: string | number, values: Record<string, string>): Promise<string> {
    const response = await apiClient.post(`${this.baseUrl}/${textId}/process`, values);
    return (response.data.data?.processedContent || response.data.processedContent) ?? '';
  }

  /**
   * Sync variables from content
   * @reserved Future use - for auto-detecting variables in content
   */
  async syncVariables(textId: string | number): Promise<StandardText> {
    const response = await apiClient.post(`${this.baseUrl}/${textId}/sync-variables`);
    return response.data.data || response.data;
  }

  /**
   * Validate variables in content
   * @reserved Future use - for validating variable usage
   */
  async validateVariables(textId: string | number): Promise<VariableValidation> {
    const response = await apiClient.get(`${this.baseUrl}/${textId}/validate-variables`);
    return response.data.data || response.data;
  }

  /**
   * Get statistics
   */
  async getStats(): Promise<StandardTextStats> {
    const response = await apiClient.get(`${this.baseUrl}/stats`);
    return response.data.data || response.data;
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await apiClient.get(`${this.baseUrl}/health`);
    return response.data.data || response.data;
  }
}

// Export singleton instance
export const standardTextService = new StandardTextService();
export default standardTextService;
